import { CalendarClock, ChevronRight, User } from 'lucide-react'
import { Body, Caption } from '@meiuca/ui-components'
import { formatCurrency, formatDateForUI, parseAPIDate } from '@meiuca/web-foundation'
import { useComponentImpression } from '@meiuca/analytics'
import { COMPONENT_TYPES } from '@/constants/analyticsEvents'
import type { PlanListItem } from '@/services/trainingCenterPlanService'

interface PlanCardProps {
  plan: PlanListItem
  onClick?: () => void
  position?: number
  /**
   * Rótulo "Aluno: <nome>" — só quando a conta enxerga mais de um aluno
   * (responsável com dependentes); itens de dependente sempre rotulados.
   */
  showStudent?: boolean
}

export function PlanCard({ plan, onClick, position, showStudent = false }: PlanCardProps) {
  const impressionRef = useComponentImpression({
    componentType: COMPONENT_TYPES.PLAN_CARD,
    componentKey: `plan_${plan.id}`,
    position,
  })
  const interactive = Boolean(onClick)
  const isActive = plan.status === 'active'

  return (
    <div
      ref={impressionRef}
      onClick={onClick}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onClick?.()
              }
            }
          : undefined
      }
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      className={`rounded-lg bg-background-white border border-border-light p-4 transition-shadow hover:shadow-md ${
        interactive ? 'cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand-primary/40' : ''
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <Body className="text-base font-semibold text-text-primary mb-1">
            {plan.trainingCenter.name}
          </Body>
          <Caption className="text-text-secondary flex items-center gap-1">
            <CalendarClock className="w-3 h-3 flex-shrink-0" />
            <span>Desde {formatDateForUI(parseAPIDate(plan.startAt), 'medium')}</span>
          </Caption>
          {(showStudent || plan.isDependent) && plan.studentCustomer && (
            <Caption className="text-text-secondary flex items-center gap-1 mt-1">
              <User className="w-3 h-3 flex-shrink-0" />
              <span>Aluno: {plan.studentCustomer.name}</span>
            </Caption>
          )}
        </div>
        <span
          className={`px-2 py-1 rounded text-xs font-medium ${
            isActive
              ? 'bg-feedback-success/20 text-feedback-success'
              : 'bg-text-tertiary/20 text-text-tertiary'
          }`}
        >
          {isActive ? 'Ativo' : 'Inativo'}
        </span>
      </div>

      <div className="flex items-center justify-between pt-3 mt-3 border-t border-border-light">
        <div>
          <Body className="text-lg font-semibold text-brand-primary m-0">
            {formatCurrency(plan.amountMonthlyCents)}
          </Body>
          <Caption className="text-text-secondary">por mês</Caption>
        </div>
        <div className="flex items-center gap-2">
          {plan.outstandingCount > 0 && (
            <span className="px-2 py-1 rounded text-xs font-medium bg-feedback-warning/20 text-feedback-warning">
              {plan.outstandingCount === 1 ? '1 a pagar' : `${plan.outstandingCount} a pagar`}
            </span>
          )}
          {interactive && <ChevronRight className="w-5 h-5 text-text-tertiary" />}
        </div>
      </div>
    </div>
  )
}
